import { AfterViewChecked, Component, ElementRef, EventEmitter, HostListener, Output, Renderer2, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { userProfile } from 'src/app/modules/auth/auth-constants';
import { AuthModule } from 'src/app/modules/auth/auth.module';
import { SurveyComponent } from 'src/app/modules/auth/signup-page/survey/survey.component';
import { catchError, of, tap } from 'rxjs';
import { profileCompletionPage, SegmentConstants, SegmentEvent } from 'src/app/shared/utilities/constants';
import { AnalyticsService } from '@laasy/common-layout';
import { MsgService } from 'src/app/shared/services/msg.service';
import { ToastService } from 'src/app/shared/services/common/toast.service';
import { SignupSurveyService } from 'src/app/modules/auth/service/signup-survey.service';
import { MissingInfoModalService } from 'src/app/shared/services/missing-info-modal.service';
import { RedirectPostLoginService } from 'src/app/modules/auth/service/redirect-post-login.service';
import { RouteRenderModalService } from 'src/app/shared/services/route-render-modal.service';

@Component({
  selector: 'app-survey-completion-modal',
  standalone: true,
  imports: [CommonModule, AuthModule],
  templateUrl: './survey-completion-modal.component.html',
  styleUrls: ['./survey-completion-modal.component.scss']
})
export class SurveyCompletionModalComponent implements AfterViewChecked {

  @Output() action = new EventEmitter<string>();
  userProfile = { ...structuredClone(userProfile), survey: [] };
  @ViewChild(SurveyComponent, { read: ElementRef }) surveyComponentRef!: ElementRef;
  @ViewChild(SurveyComponent) surveyComponent!: SurveyComponent;
  isSkipable: boolean = false;

  parentStyle = {
    width: '100%',
    height: '46px',
    'border-radius': '24px',
    display: 'flex',
    'justify-content': 'center',
    'align-items': 'center',
    'margin-top': '20px',
    gap: '1rem',
    padding: '0',
  };

  buttonMobileStyle = {
    width: '100%',
    height: '46px',
  }

  parentMobileStyle = {
    'flex-direction': 'column-reverse',
    'padding-top': '1rem',
    'padding-bottom': '1rem',
    height: '150px',
  }

  submitButtonStyles = {
    width: '50%',
    margin: 'auto',
    padding: '0',
    height: '100%',
  };

  skipButtonStyles = {
    width: '50%',
    height: '46px',
    'border-radius': '24px',
    'border-color': 'black',
    background: '#FFFFFF',
    cursor: 'pointer',
  };

  submitSurvey = async () => {
    this.surveyComponent.formNavComponent.isValid = false
    const surveyQues = this.surveyComponent.surveys.map((x: any) => x.question).join(', ')
    this.surveyService.updateSurveyResponse(this.userProfile.survey!).pipe(
      tap(() => {
        this.action.emit(profileCompletionPage.action.submitted);
        this.analyticsService.segmentTrackEvent({ action: SegmentConstants.surveySubmit, question: surveyQues }, SegmentEvent.profileCompletionPage);
        this.msgService.success(profileCompletionPage.message.success)
        this.redirectPostLogin.redirectPostLogin(this.userProfile.email, this.userProfile)
      }),
      catchError((err) => {
        this.analyticsService.segmentTrackEvent({ action: SegmentConstants.surveySubmitError, question: surveyQues, error: err }, SegmentEvent.profileCompletionPage);
        this.surveyComponent.formNavComponent.isValid = true
        this.toast.errorToast(profileCompletionPage.message.surveySubmitError)
        return of(err)
      })
    ).subscribe()
  }

  constructor(private renderer: Renderer2, private analyticsService: AnalyticsService, private msgService: MsgService, private toast: ToastService, private surveyService: SignupSurveyService, private missingInfoModalService: MissingInfoModalService, private redirectPostLogin: RedirectPostLoginService, private routeRenderModalService: RouteRenderModalService) { }

  ngAfterViewChecked(): void {
    this.isSkipable = this.missingInfoModalService.isSkipable;
    if (!this.surveyComponent || !this.surveyComponentRef) return;

    this.hideBackButton();
    const container = this.surveyComponentRef.nativeElement.querySelector('.container');
    if (container) {
      this.renderer.setStyle(container, 'padding', '0px');
    }

    const parent = this.updateBackButton();
    this.updateSubmitButton();
    this.updateSkipButton(parent);

    const flexContainer = this.surveyComponentRef.nativeElement.querySelector('.influencers-section') as HTMLElement;
    if(!flexContainer) return;

    if(!flexContainer?.parentElement?.style) return;
    Object.assign(flexContainer.parentElement.style, { width: '100%', padding: '0', maxWidth:'100%' });

    Object.assign(flexContainer.style, {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(123px, 1fr))',
      gap: '16px',
      alignItems: 'center',
      justifyItems: 'center',
      padding: '0'
    });

    flexContainer.querySelectorAll('.influencers').forEach((el: any) => el.style.width = '123px');
  }

  hideBackButton(){
    const circleElement = this.surveyComponentRef.nativeElement.querySelector('.circle');
    if (circleElement) {
      this.renderer.setStyle(circleElement, 'display', 'none');
    }
  }

  updateBackButton() {
    const backButton = this.surveyComponentRef.nativeElement.querySelector('.backButton');
    const parent = backButton?.parentElement;
    if(!parent) return;

    this.applyStyles(parent, {
      ...this.parentStyle,
      ...(this.isMobile ? this.parentMobileStyle : {})
    });
    this.renderer.setStyle(backButton, 'display', 'none');
    return parent;
  }

  updateSubmitButton() {
    const submitButton = this.surveyComponentRef.nativeElement.querySelector('.submitButton');
    const containerFormNavButton = this.surveyComponentRef.nativeElement.querySelector('.container-form-nav-button');
    if (containerFormNavButton)
      this.renderer.setStyle(containerFormNavButton, 'padding', '0');

    this.applyStyles(submitButton, {
      ...this.submitButtonStyles,
      width: this.isSkipable ? '50%' : '100%',
      ...(this.isMobile ? this.buttonMobileStyle : {})
    });
    this.renderer.listen(submitButton, 'click', () => this.action.emit('continue'));
    this.renderer.setStyle(submitButton.firstChild, 'margin', '0');

    const surveyFormIndex = this.surveyComponent.survey_current_indx
    const totalSurveys = this.surveyComponent.surveys.length
    if (surveyFormIndex === totalSurveys - 1) {
      this.surveyComponent.formNavComponent.handleContinueClick = this.submitSurvey
    }
  }

  updateSkipButton(parent: any) {
    // Check if skip button already exists
    const existingSkipButton = parent?.querySelector('.skip-button');
    if (existingSkipButton || !this.isSkipable) return;

    const skipButton = this.renderer.createElement('button');
    const skipButtonId = `skip-btn-${Date.now()}`;
    this.renderer.setAttribute(skipButton, 'id', skipButtonId);
    this.renderer.addClass(skipButton, 'skip-button');
    this.applyStyles(skipButton, {
      ...this.skipButtonStyles,
      ...(this.isMobile ? this.buttonMobileStyle : {})
    });

    const mediaStyle = this.renderer.createElement('style');
    this.renderer.setProperty(mediaStyle, 'textContent', `@media screen and (min-width: 960px) { #${skipButtonId} { width: 100%; } }`);
    this.renderer.appendChild(document.head, mediaStyle);

    this.renderer.setProperty(skipButton, 'textContent', 'Skip');
    this.renderer.listen(skipButton, 'click', () => this.action.emit('skip'));

    const firstChild = parent.firstChild;
    this.renderer.insertBefore(parent, skipButton, firstChild);
  }

  private applyStyles(parent: HTMLElement, styles: any) {
    Object.keys(styles).forEach(key => {
      this.renderer.setStyle(parent, key, styles[key as keyof typeof styles]);
    });
  }

  get isMobile(){
    return window.innerWidth < 960;
  }

  @HostListener('window:scrollToSubHeadingEvent', ['$event'])
  onScrollToSubHeading(event: any) {
    if (event?.detail?.calledFrom === 'circularSelection') {
      const dialogRef = this.routeRenderModalService?.dialogRef;
      const dialogElement = dialogRef?.dialogElement?.nativeElement?.querySelector('.p-dialog-content');
      if (dialogElement && typeof dialogElement.scrollTo === 'function') {
        dialogElement.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }
}
